/* eslint-disable no-console */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CustomModal from "../../../Core/Components/Modal/CustomModal";
import SpoiltMilkIcon from "../../../Common/Svgs/SpoiltMilkIcon";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import Button from "../../../Core/Components/CustomButton";
import CustomInput from "../../../Core/Components/CustomInput";
import AlertIcon from "../../../Common/Svgs/AlertIcon";
import { client } from "../../../Utils/axiosClient";
import { getTripDetailsKPI } from "../../../Redux/APIs/tripsAPI";
import { updateTrips } from "../../../Redux/Slices/trips.slice";
import CustomSwitch from "../../../Core/Components/CustomSwitch";

const isValidNumber = (value) => {
  // Check if empty or only whitespace
  if (!value || value.trim() === "") return false;

  // Check if it's a valid number
  const num = Number(value);
  return !Number.isNaN(num) && Number.isFinite(num);
};

const SpoiltMilkModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { spoiltMilkModal, tripDetailsKPI } = useSelector(
    (state) => state.trips,
  );

  const { id } = useParams();

  const [checked, setChecked] = useState(
    Number(tripDetailsKPI?.spoiltByDriver) === 1,
  );

  const old =
    parseFloat(spoiltMilkModal?.stops || 0) +
    parseFloat(spoiltMilkModal?.excess || 0);

  const [newWeight, setNewWeight] = useState(0);
  const [focus, setFocus] = useState(false);
  const [error, setError] = useState("");

  const renderBorderColor = () => {
    if (error) return "error";
    if (focus) return "success";
    return "primary";
  };

  const onSave = async () => {
    if (!newWeight) {
      setError("Please enter new weight");
      return;
    }

    if (!isValidNumber(newWeight)) {
      setError("Please enter a valid value");
      return;
    }

    if (parseFloat(newWeight) < parseFloat(old)) {
      setError("New weight must be more than original weight");
      return;
    }

    try {
      dispatch(updateTrips({ key: "loading", value: true }));

      const payload = {
        tripId: id,
        oldValue: old,
        newValue: +newWeight,
        spoiltByDriver: checked,
      };

      await client.post(
        `${process.env.REACT_APP_API_URL}/trip/excess-spoilt-milk`,
        payload,
      );

      dispatch(getTripDetailsKPI({ tripId: id }));
    } catch (err) {
      console.log(err);
    } finally {
      setNewWeight(0);
      onCancel();
      dispatch(updateTrips({ key: "loading", value: false }));
      setChecked(false);
    }
  };

  return (
    <CustomModal visible={visible} onCancel={onCancel} width="536px">
      <div
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="flex items-center justify-between px-4 pt-4">
          <div className="flex items-center gap-x-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary">
              <SpoiltMilkIcon size={24} className="text-background-white" />
            </div>

            <p className="font-poppins font-medium text-base text-black">
              Update Spoilt Milk
            </p>
          </div>

          <button type="button" className="mr-2" onClick={onCancel}>
            <CloseIcon className="stroke-dark-gray w-6 h-6" />
          </button>
        </div>

        <div className="mt-6 px-6 pb-6 flex items-center justify-between border-b border-light-grey">
          <div>
            <p className="font-poppins font-medium text-xs">
              Spoilt Milk Reason
            </p>

            <p className="mt-1 font-aileron font-normal text-xs text-dark-gray">
              Was it spoilt by driver?
            </p>
          </div>

          <CustomSwitch
            variant="spoilt-milk"
            checkedChildren="Yes"
            unCheckedChildren="No"
            checked={checked}
            onChange={() => setChecked((prev) => !prev)}
          />
        </div>

        <div className="mt-6 px-6">
          <h1 className="font-poppins text-xs font-medium text-black">
            Edit Spoilt Milk
          </h1>

          <p className="mt-1 font-aileron text-xs font-normal text-dark-gray">
            Please enter the new weight of total spoilt milk weighed.
          </p>
        </div>

        <div className="mt-4 bg-background py-3 px-6 flex items-center">
          <p className="flex-1 font-aileron font-normal text-10 text-dark-gray">
            Original Weight
          </p>

          <p className="flex-1 font-aileron font-normal text-10 text-dark-gray">
            New Weight
          </p>
        </div>

        <div className="flex items-center justify-between py-3 px-6 border-b border-light-grey">
          <p className="flex-1 font-aileron font-semibold text-xs text-dark-gray capitalize">
            {parseFloat(old).toFixed(2)} L
          </p>

          <div className="flex-1 flex items-center">
            <CustomInput
              intent="editSpoiltMilk"
              text="generalSettings"
              value={newWeight || ""}
              focus={focus}
              border={renderBorderColor()}
              placeholder={focus ? null : "Enter weight"}
              onFocus={() => setFocus(true)}
              onBlur={() => setFocus(false)}
              suffix={error ? <AlertIcon className="text-alert-red" /> : null}
              onChange={(e) => {
                setNewWeight(e.target.value);
                setError("");
              }}
            />
            <p className="ml-2 flex-1 font-aileron font-semibold text-xs text-dark-gray capitalize">
              L
            </p>
          </div>
        </div>

        {error ? (
          <p className="ml-6 mt-1 font-aileron font-semibold text-10 text-alert-red">
            {error}
          </p>
        ) : (
          <div className="ml-6 mt-1 h-4" />
        )}

        <div className="p-6 flex items-center justify-end gap-x-3">
          <Button colors="grey" size="primary" onClick={onCancel}>
            Cancel
          </Button>

          <Button colors="primary" size="primary" onClick={onSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default SpoiltMilkModal;
